import Phaser from 'phaser';
import { DIRT } from './theme';

/** The dirt trail's width — every style keeps the same footprint. */
export const TRAIL_WIDTH = 28;

export type TrailStyle = 'railway' | 'rocks' | 'panel' | undefined;

/**
 * Draw one polyline in a level's trail style. GameScene and the Level
 * Creator's editor share this, so a creator level looks like the real thing.
 */
export function drawTrailStyle(
  gfx: Phaser.GameObjects.Graphics,
  pts: { x: number; y: number }[],
  style: TrailStyle,
): void {
  if (style === 'railway') drawRailway(gfx, pts);
  else if (style === 'rocks') drawRockTrail(gfx, pts);
  else if (style === 'panel') drawPanelTrail(gfx, pts);
  else {
    gfx.lineStyle(TRAIL_WIDTH, DIRT, 1);
    gfx.beginPath();
    gfx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) gfx.lineTo(pts[i].x, pts[i].y);
    gfx.strokePath();
  }
}

/**
 * Railway trail (`trackStyle: 'railway'`): a gravel bed, wooden sleepers
 * perpendicular to the track, and two steel rails offset either side of the
 * centreline. Corner offsets are mitred, which is exact for the axis-aligned
 * 90° paths every level uses. Pure Graphics — overlap-e2e safe.
 */
export function drawRailway(
  gfx: Phaser.GameObjects.Graphics,
  waypoints: { x: number; y: number }[],
): void {
  const BED_W = TRAIL_WIDTH; // gravel bed matches the dirt trail's footprint
  const TIE_W = 24; // sleeper length (across the track)
  const TIE_T = 6; // sleeper thickness (along the track)
  const TIE_SPACING = 24;
  const RAIL_OFFSET = 7; // rail distance from the centreline
  const RAIL_T = 3;

  // Gravel bed under everything.
  gfx.lineStyle(BED_W, 0x4a4136, 1);
  gfx.beginPath();
  gfx.moveTo(waypoints[0].x, waypoints[0].y);
  for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
  gfx.strokePath();

  // Per-segment unit direction + perpendicular.
  const dirs = [];
  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    dirs.push({ a, b, len, ux, uy, px: -uy, py: ux });
  }

  // Wooden sleepers, evenly spaced along each segment.
  gfx.lineStyle(TIE_T, 0x6a4a26, 1);
  for (const d of dirs) {
    for (let t = TIE_SPACING / 2; t <= d.len - TIE_SPACING / 4; t += TIE_SPACING) {
      const cx = d.a.x + d.ux * t;
      const cy = d.a.y + d.uy * t;
      gfx.lineBetween(
        cx - (d.px * TIE_W) / 2,
        cy - (d.py * TIE_W) / 2,
        cx + (d.px * TIE_W) / 2,
        cy + (d.py * TIE_W) / 2,
      );
    }
  }

  // Twin steel rails: the centreline offset ±RAIL_OFFSET, with mitred
  // corners (perpendiculars of both adjoining segments summed at a vertex).
  gfx.lineStyle(RAIL_T, 0x9aa2ad, 1);
  for (const side of [-1, 1]) {
    gfx.beginPath();
    for (let i = 0; i < waypoints.length; i++) {
      const prev = dirs[i - 1];
      const next = dirs[i];
      // Corner vertices sum two unit perpendiculars — for 90° axis-aligned
      // turns that is the exact mitre; endpoints use their one segment's.
      const ox = ((prev?.px ?? 0) + (next?.px ?? 0)) * side * RAIL_OFFSET;
      const oy = ((prev?.py ?? 0) + (next?.py ?? 0)) * side * RAIL_OFFSET;
      if (i === 0) gfx.moveTo(waypoints[i].x + ox, waypoints[i].y + oy);
      else gfx.lineTo(waypoints[i].x + ox, waypoints[i].y + oy);
    }
    gfx.strokePath();
  }
}

/**
 * Rock trail (`trackStyle: 'rocks'`, Fiery Lava): big cooled-basalt boulders
 * laid across the lava with a worn TRACK trodden along the top of them, so
 * the crossing reads as a rock causeway that the horde actually walks —
 * boulders first, then the path over them. Stone size and jitter come from
 * the stone's index (deterministic — no RNG, so it is stable across reloads
 * and screenshot diffs). Pure Graphics — overlap-e2e safe.
 */
export function drawRockTrail(
  gfx: Phaser.GameObjects.Graphics,
  waypoints: { x: number; y: number }[],
): void {
  const BED_W = TRAIL_WIDTH + 18; // basalt causeway, wider than the boulders
  const STEP = 54; // centre-to-centre spacing — sparse, chunky stepping stones
  const R = 29; // base boulder radius — must clearly overhang the track
  const TRACK_W = 13; // the worn path trodden over the boulder tops

  // Deterministic pseudo-random in [0,1) from two integers — no RNG, so the
  // causeway is identical across reloads and screenshot diffs.
  const rand = (n: number, k: number): number => {
    const v = Math.sin(n * 127.1 + k * 311.7) * 43758.5453;
    return v - Math.floor(v);
  };

  /**
   * One irregular boulder: a closed polygon of `verts` points whose radii and
   * angles are individually jittered, so no two stones share a silhouette.
   */
  const boulder = (cx: number, cy: number, r: number, n: number): void => {
    const verts = 6 + Math.floor(rand(n, 1) * 4); // 6–9 sides
    const spin = rand(n, 2) * Math.PI * 2;
    const pts: Phaser.Geom.Point[] = [];
    for (let v = 0; v < verts; v++) {
      // Angle wobbles around the even division so sides differ in width.
      const ang = spin + ((v + (rand(n, 10 + v) - 0.5) * 0.55) / verts) * Math.PI * 2;
      const rad = r * (0.68 + rand(n, 40 + v) * 0.5);
      pts.push(new Phaser.Geom.Point(cx + Math.cos(ang) * rad, cy + Math.sin(ang) * rad));
    }
    // Shadowed underside first, offset down-right to seat the rock.
    gfx.fillStyle(0x241f1c, 1);
    gfx.fillPoints(
      pts.map((p) => new Phaser.Geom.Point(p.x + 3, p.y + 4)),
      true,
    );
    gfx.fillStyle(0x4c443c, 1); // body
    gfx.fillPoints(pts, true);
    // Lit top-left face: the same silhouette shrunk toward an offset centre,
    // so the facet follows the rock's own shape instead of being a disc.
    const fx = cx - r * 0.2;
    const fy = cy - r * 0.22;
    gfx.fillStyle(0x6b6157, 1);
    gfx.fillPoints(
      pts.map((p) => new Phaser.Geom.Point(fx + (p.x - cx) * 0.58, fy + (p.y - cy) * 0.58)),
      true,
    );
    gfx.fillStyle(0x837a6f, 1); // highlight catching the lava glow
    gfx.fillPoints(
      pts.map((p) => new Phaser.Geom.Point(fx + (p.x - cx) * 0.26, fy + (p.y - cy) * 0.26)),
      true,
    );
  };

  // Dark basalt bed, so gaps between the sparse stones never show raw lava.
  gfx.lineStyle(BED_W, 0x2b2622, 1);
  gfx.beginPath();
  gfx.moveTo(waypoints[0].x, waypoints[0].y);
  for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
  gfx.strokePath();

  // Walk the whole polyline at a fixed stride, dropping one boulder per step.
  let n = 0;
  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    const px = -uy;
    const py = ux;
    for (let t = 0; t <= len; t += STEP, n++) {
      // Offset across and along the trail so the chain never looks stamped.
      const cx = a.x + ux * (t + (rand(n, 3) - 0.5) * 14) + px * (rand(n, 4) - 0.5) * 22;
      const cy = a.y + uy * (t + (rand(n, 3) - 0.5) * 14) + py * (rand(n, 4) - 0.5) * 22;
      boulder(cx, cy, R * (0.72 + rand(n, 5) * 0.8), n); // sizes vary ~0.7–1.5×
    }
  }

  // The worn track over the boulder tops — laid last so it reads as a path
  // trodden ALONG the rocks rather than a road beside them.
  gfx.lineStyle(TRACK_W, DIRT, 1);
  gfx.beginPath();
  gfx.moveTo(waypoints[0].x, waypoints[0].y);
  for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
  gfx.strokePath();
}

/**
 * Panel trail (`trackStyle: 'panel'`, The Space Station): a walkway of
 * darker hull plating with a seam either side and rivets along both edges,
 * so the route blends into the satellite instead of being painted dirt.
 */
export function drawPanelTrail(
  gfx: Phaser.GameObjects.Graphics,
  waypoints: { x: number; y: number }[],
): void {
  const stroke = (width: number, color: number, alpha: number) => {
    gfx.lineStyle(width, color, alpha);
    gfx.beginPath();
    gfx.moveTo(waypoints[0].x, waypoints[0].y);
    for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
    gfx.strokePath();
  };
  stroke(TRAIL_WIDTH + 6, 0x5f6670, 1); // outer seam
  stroke(TRAIL_WIDTH, 0x8f979f, 1); // the walkway plate
  stroke(TRAIL_WIDTH - 12, 0x9aa2aa, 1); // a lighter centre strip
  // Rivets every 40px along each segment, on both shoulders.
  gfx.fillStyle(0x4b525a, 1);
  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1];
    const b = waypoints[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    const nx = -uy * (TRAIL_WIDTH / 2 - 3);
    const ny = ux * (TRAIL_WIDTH / 2 - 3);
    for (let d = 20; d < len - 10; d += 40) {
      const px = a.x + ux * d;
      const py = a.y + uy * d;
      gfx.fillCircle(px + nx, py + ny, 2);
      gfx.fillCircle(px - nx, py - ny, 2);
    }
  }
}
